/**
 * Sesión verificada de Galia (fases 2-3 del plan docs/security/lti-auth-plan.md).
 *
 * Flujo: el lanzamiento LTI verificado redirige con `?session=<token de 10 min>`;
 * aquí se canjea contra /api/session/redeem por un token de sesión de 7 días +
 * la identidad (email, user_id, roles LTI, context_id). Se guarda en localStorage
 * para que las reaperturas de la PWA (días después, sin pasar por Apolearn)
 * sigan identificadas, y se borra el token de la barra de direcciones para que
 * copiar/compartir el URL no regale la sesión.
 *
 * La identidad ya NO se lee de parámetros planos del URL (email/roles/user_id):
 * ese era el hueco de seguridad. El único bypass es `?force_role=` para QA, y
 * solo en dev (testingPhase) — ver authService.getEffectiveRole().
 */

export interface SessionIdentity {
  email?: string;
  user_id?: string;
  full_name?: string;
  roles?: string;
  context_id?: string;
  /** Tokens de acceso de Apolearn si el launch los trajo (LTI 1.1 normalmente no). */
  token?: string;
  refresh?: string;
  /** true si el lanzamiento pasó la verificación de firma OAuth en el servidor. */
  verified: boolean;
  /**
   * Por dónde entró la sesión. Ausente = 'lti' (todo lo emitido antes del
   * acceso directo). 'direct' = registro por correo validado contra AEC.
   * Ver docs/security/acceso-directo-fase1-plan.md.
   */
  origin?: 'lti' | 'direct';
  /**
   * Nivel MCER base del curso en AEC ('A1'…'C2'), solo en sesiones 'direct'.
   * Sustituye al `context_id` de Apolearn para preseleccionar contenido; NO
   * cambia el nivel de interacción, que sigue eligiendo el estudiante.
   */
  nivel?: string;
}

interface StoredSession {
  /** JWT scope 'session' (7 días) — credencial para endpoints propios (fase 4). */
  token: string;
  identity: SessionIdentity;
  /** exp del JWT en segundos epoch, para descartar sesiones vencidas sin decodificar. */
  exp: number;
}

const STORAGE_KEY = 'galia_session';

class SessionService {
  private current: StoredSession | null = null;
  private resolved = false;
  /**
   * Sesión guardada que ya venció (acuerdo Santiago 01/09/2026: 1 semana). Se
   * conserva para que App.tsx muestre "sesión vencida" en vez de dejar caer al
   * estudiante a modo anónimo en silencio (chatearía sin quedar registrado en el
   * dashboard). Solo se llena cuando NO hay sesión nueva que la reemplace.
   */
  private expired: StoredSession | null = null;

  /**
   * Resuelve la sesión al arrancar la app (una vez): canjea el `?session=` del
   * URL si viene de un lanzamiento LTI, o cae a la sesión guardada (PWA
   * reabierta). Devuelve la identidad o null (acceso anónimo / formulario).
   */
  async resolve(): Promise<SessionIdentity | null> {
    if (this.resolved) {
      return this.current?.identity ?? null;
    }
    this.resolved = true;

    const url = new URL(window.location.href);
    const launchToken = url.searchParams.get('session');

    if (launchToken) {
      // NO se quita el `?session=` del URL todavía. La app se auto-recarga tras
      // un deploy nuevo (useVersionCheck); si esa recarga cayera entre "quité el
      // token" y "guardé la sesión", al recargar no habría ni token en el URL ni
      // sesión en localStorage → el usuario quedaría anónimo y "¡A practicar!"
      // volvería a la configuración. Por eso el token permanece en el URL hasta
      // que la sesión esté PERSISTIDA (una recarga a mitad de canje lo reintenta),
      // o hasta que el servidor lo rechace de forma definitiva (token muerto).
      let tokenSpent = false;
      try {
        const res = await fetch('/api/session/redeem', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ launch: launchToken }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.session && data?.identity) {
            const stored: StoredSession = {
              token: data.session,
              identity: data.identity,
              exp: this.readExp(data.session),
            };
            this.current = stored;
            this.save(stored); // persistir ANTES de tocar el URL
            tokenSpent = true;
          }
        } else {
          // 401 = token inválido/expirado: definitivamente muerto, se puede
          // quitar del URL. Otros códigos (5xx) se tratan como transitorios.
          if (res.status === 401) tokenSpent = true;
          console.warn(`[Session] Canje del token de lanzamiento rechazado (HTTP ${res.status}); se usa la sesión guardada si existe`);
        }
      } catch (err) {
        // Error de red (sin respuesta): transitorio. Se deja el token en el URL
        // para que la próxima carga/recarga lo reintente.
        console.error('[Session] Error canjeando el token de lanzamiento:', err);
      }

      // Quitar el token del URL solo cuando ya no hace falta: sesión guardada, o
      // token rechazado. En fallo transitorio se conserva para reintentar.
      if (tokenSpent) {
        url.searchParams.delete('session');
        try {
          window.history.replaceState(null, '', url.toString());
        } catch { /* noop */ }
      }

      if (this.current) return this.current.identity;
    }

    // Sin lanzamiento nuevo (o canje fallido): sesión guardada de una visita anterior.
    this.current = this.load();
    return this.current?.identity ?? null;
  }

  /**
   * Adopta la sesión que devuelve /api/auth/verify-code (acceso directo).
   *
   * Es el equivalente del canje LTI para la puerta por correo: el servidor ya
   * verificó el código y firmó el token, aquí solo se guarda. Marca `resolved`
   * para que un resolve() posterior no lo pise con la sesión vieja.
   */
  adopt(token: string, identity: SessionIdentity): void {
    const stored: StoredSession = { token, identity, exp: this.readExp(token) };
    this.current = stored;
    this.resolved = true;
    this.save(stored);
  }

  /**
   * Identidad de la sesión que venció (tras resolve()), o null si no había
   * sesión guardada o si sigue vigente. Sirve para saludar por nombre en la
   * pantalla de sesión vencida y para elegir el idioma según el rol LTI.
   */
  getExpiredIdentity(): SessionIdentity | null {
    return this.current ? null : this.expired?.identity ?? null;
  }

  /**
   * Lectura síncrona del estado guardado, sin efectos: 'active' (sesión
   * vigente), 'expired' (guardada pero vencida) o 'none'. Permite que App.tsx
   * decida la primera pantalla sin esperar a resolve() ni parpadear.
   */
  peekStoredState(): 'active' | 'expired' | 'none' {
    const raw = this.read();
    if (!raw) return 'none';
    return this.isExpired(raw) ? 'expired' : 'active';
  }

  /**
   * Por dónde entró la sesión guardada (viva o vencida), leído de forma
   * síncrona de localStorage; null si no hay ninguna. Lo usa
   * `isDirectAccessMode()` para decidir si la raíz del sitio debe mostrar la
   * puerta de correo + código: una sesión LTI guardada sigue su propio camino
   * (incluida la pantalla de "sesión vencida"), una directa o ninguna, no.
   */
  peekStoredOrigin(): 'lti' | 'direct' | null {
    const raw = this.read();
    if (!raw) return null;
    return raw.identity?.origin === 'direct' ? 'direct' : 'lti';
  }

  /**
   * Identidad de la sesión guardada si está vencida, leída de forma síncrona
   * de localStorage. La pantalla de sesión vencida se pinta ANTES de que
   * resolve() corra (arranque síncrono en App.tsx) y React no vuelve a
   * renderizar si el estado no cambia, así que no puede depender de
   * getExpiredIdentity(); esta lectura directa siempre trae la identidad.
   */
  peekExpiredIdentity(): SessionIdentity | null {
    if (this.current) return null;
    const raw = this.read();
    return raw && this.isExpired(raw) ? raw.identity : null;
  }

  /**
   * Tras "sesión vencida": ¿apareció una sesión vigente en localStorage? Pasa
   * cuando el estudiante vuelve a entrar por Apolearn en el navegador (misma
   * origin) y regresa a la app instalada. El llamador recarga la página para
   * arrancar limpio con ella.
   */
  hasFreshStoredSession(): boolean {
    const raw = this.read();
    return !!raw && !this.isExpired(raw);
  }

  /** Identidad de la sesión activa (tras resolve()), o null. */
  getIdentity(): SessionIdentity | null {
    return this.current?.identity ?? null;
  }

  /** Token de sesión (scope 'session') para autenticar endpoints propios (fase 4). */
  getSessionToken(): string | null {
    return this.current?.token ?? null;
  }

  /** Cierra la sesión local (QA / depuración). */
  clear(): void {
    this.current = null;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch { /* noop */ }
  }

  private readExp(jwt: string): number {
    try {
      const body = jwt.split('.')[1] || '';
      const json = atob(body.replace(/-/g, '+').replace(/_/g, '/'));
      const payload = JSON.parse(json);
      return typeof payload.exp === 'number' ? payload.exp : 0;
    } catch {
      return 0;
    }
  }

  private save(session: StoredSession): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch { /* noop (modo privado / storage lleno) */ }
  }

  private read(): StoredSession | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const session = JSON.parse(raw) as StoredSession;
      if (!session?.token || !session?.identity) return null;
      return session;
    } catch {
      return null;
    }
  }

  private isExpired(session: StoredSession): boolean {
    return !!session.exp && Date.now() / 1000 > session.exp;
  }

  private load(): StoredSession | null {
    const session = this.read();
    if (!session) return null;
    // Sesión vencida (7 días): no se usa, pero NO se borra. Si se borrara, la
    // siguiente recarga ya no sabría que hubo sesión y dejaría pasar al
    // estudiante como anónimo; conservándola, cada carga vuelve a mostrar
    // "sesión vencida" hasta que un lanzamiento LTI nuevo la sobrescriba.
    if (this.isExpired(session)) {
      this.expired = session;
      return null;
    }
    return session;
  }
}

export const sessionService = new SessionService();
export default sessionService;
